// CORE-496 — home-page onboarding tour: step configuration.
//
// Texts are the DRAFT copy from the spec (RU). Final wording + ToV, and the
// EN/ES localisation, are a separate task (see docs/agapella-companion-status.md
// §3.1). Kept as data so the overlay stays dumb and the steps are unit-testable.
//
// CORE-1350 (П1, М1) — сверка, ради которой стояла пометка ниже, состоялась.
//
// Пометка гласила: «the spec labels masters "Проводники Света" in step 2. Per
// the core concept, «Проводник света» is a gamification LEVEL, not a master
// role — this copy is flagged for reconciliation before final ToV sign-off».
// Сверка не случилась, а черновик уехал на прод и попал в отчёт от 08.09
// дословно: «Мастера / Это Проводники Света — люди, которые делятся своим
// опытом, знаниями и практиками».
//
// Важное для истории: CORE-960 трижды правил промпты Агапеллы и трижды не
// удерживал этот дефект — потому что чинить было нечего. Текст, который видит
// человек, лежит здесь, в клиентском бандле, и никакой блок в контексте модели
// до него не дотягивается.
//
// Заодно ушёл «Искатель» из приветствия: тур здоровается со всеми, а этап у
// людей разный. Мастеру этап не положен вовсе (он вне Пути света), а участнику
// на «Хранителе» первый уровень в лицо — такая же ошибка, просто менее заметная.

export type TourAnchorKey = 'masters' | 'practices' | 'profile';

export interface TourStep {
  /** 1-based step index (matches backend currentStep). */
  id: number;
  /** Home block to spotlight; null = centered card (welcome / completion). */
  anchor: TourAnchorKey | null;
  title: string;
  body: string;
  /** Shown briefly when the user skips this step (steps 2–4). */
  skipBody?: string;
  primaryLabel: string;
  secondaryLabel: string;
  /** 'skip_all' fully skips the tour; 'skip_step' advances; 'chat' opens Agapella. */
  secondaryAction: 'skip_all' | 'skip_step';
  primaryAction: 'next' | 'complete' | 'chat';
}

// CORE-1750 (возврат QA 23.10) — имя собеседницы в русском тексте пишется
// кириллицей: «Агапелла», как в списке чатов, настройках и её приветствии в
// чате; латиница «Agapella» — это платформа и логотип. Иначе имя не склоняется
// («чат с Агапеллой»), а в чате шли рядом «Я Агапелла…» и «Я — Agapella…».
// Решение — ответ Fable в отчёте к задаче.
export const TOUR_STEPS: readonly TourStep[] = [
  {
    id: 1,
    anchor: null,
    title: 'Привет ✦',
    body:
      'Я — Агапелла, твой проводник на пути внутреннего развития.\n\n' +
      'Позволь мне за несколько минут показать пространство, которое мы создали ' +
      'для поиска знаний, практик и единомышленников.\n\n' +
      'В любой момент ты сможешь пропустить знакомство или вернуться к нему позже.',
    primaryLabel: 'Начать знакомство',
    primaryAction: 'next',
    secondaryLabel: 'Пропустить',
    secondaryAction: 'skip_all',
  },
  {
    id: 2,
    anchor: 'masters',
    title: 'Мастера',
    body:
      'Это мастера — люди, которые делятся своим опытом, знаниями и практиками.\n\n' +
      'Здесь ты можешь познакомиться с их направлениями, посмотреть события и выбрать ' +
      'тех, чей путь тебе наиболее близок.',
    skipBody:
      'Хорошо 🌿\n\nС мастерами ты сможешь познакомиться позже.\n\n' +
      'Давай тогда посмотрим, какие практики уже доступны на платформе.',
    primaryLabel: 'Далее',
    primaryAction: 'next',
    secondaryLabel: 'Пропустить этот шаг',
    secondaryAction: 'skip_step',
  },
  // CORE-1520 — Live-сессии живут в шаге практик, а не отдельным шагом.
  //
  // Отдельный шаг значил бы шестой, а бэкенд хранит у каждого человека
  // TotalSteps = 5 и прижимает к нему номер шага (OnboardingTourService):
  // шестой записался бы пятым, и в аналитике тура профиль и завершение слились
  // бы в один номер. Да и спека CORE-496 держит тур в пяти шагах.
  //
  // Абзац про Live — второй, сразу за «Здесь собраны практики»: третьим он уходил
  // под сгиб карточки тура в виджете, и без прокрутки его не было видно.
  //
  // Место выбрано по смыслу: рядом с библиотекой записей проще всего сказать,
  // чем Live-сессия от неё отличается. Раздел назван так, как он подписан в
  // меню («Календарь», `nav.schedule`) — это и есть ответ на «как попасть».
  // Слова «эфир» и «комната» здесь нельзя: термин на платформе один —
  // «Live-сессия» (сторож liveTermSweep-CORE853).
  {
    id: 3,
    anchor: 'practices',
    title: 'Практики и Live-сессии',
    body:
      'Здесь собраны практики различных направлений.\n\n' +
      'Записи ждут тебя в любое время, а Live-сессии идут прямо сейчас: можно прийти ' +
      'к мастеру или собрать своих и практиковать вместе. ' +
      'Ближайшие — в «Календаре».\n\n' +
      'Со временем я научусь лучше понимать твои интересы и смогу подбирать именно те ' +
      'практики, которые будут наиболее полезны именно тебе.',
    skipBody:
      'Конечно 😊\n\nПрактики всегда будут ждать тебя здесь, а ближайшие Live-сессии — ' +
      'в «Календаре».\n\n' +
      'А сейчас я покажу ещё одну важную часть платформы.',
    primaryLabel: 'Далее',
    primaryAction: 'next',
    secondaryLabel: 'Пропустить этот шаг',
    secondaryAction: 'skip_step',
  },
  {
    id: 4,
    anchor: 'profile',
    title: 'Твой профиль',
    body:
      'Любой путь начинается со знакомства с собой.\n\n' +
      'Заполнив профиль, ты поможешь мне лучше понять твои интересы, цели и предпочтения.\n\n' +
      'Благодаря этому я смогу делать рекомендации гораздо точнее и полезнее именно для тебя.',
    skipBody:
      'Ничего страшного 🌱\n\nЗаполнить профиль можно в любое удобное время.\n\n' +
      'Даже небольшая информация поможет мне лучше сопровождать тебя на этом пути.',
    primaryLabel: 'Далее',
    primaryAction: 'next',
    secondaryLabel: 'Пропустить этот шаг',
    secondaryAction: 'skip_step',
  },
  {
    id: 5,
    anchor: null,
    title: 'Спасибо, что уделил время знакомству ✨',
    body:
      'Теперь ты знаешь самое главное о платформе.\n\n' +
      'Если возникнут вопросы или понадобится помощь — я всегда рядом.',
    primaryLabel: 'Перейти в чат с Агапеллой',
    primaryAction: 'chat',
    secondaryLabel: 'Завершить знакомство',
    secondaryAction: 'skip_step', // here "secondary" just closes/completes
  },
] as const;

export const TOTAL_TOUR_STEPS = TOUR_STEPS.length;

/**
 * Copy Agapella leaves behind when the tour ends early. Both are shown in the
 * pop-up as her last line, then the pop-up collapses into the persistent
 * launcher icon (spec §«Пропуск онбординга» / §«Закрытие онбординга»).
 */
export const TOUR_FAREWELL_SKIPPED =
  'Хорошо 🌿\n\nПознакомиться с платформой можно позже — просто открой меня, и мы начнём.';

export const TOUR_FAREWELL_CLOSED =
  'Я запомнила, где мы остановились 🌿\n\nВернуться к знакомству можно в любой момент — просто открой меня.';

/** How the step reads as one message in the Agapella chat history. */
export function stepTranscript(step: TourStep): string {
  return `${step.title}\n\n${step.body}`;
}

/** Look up a step by its 1-based id. Returns undefined for out-of-range ids. */
export function getTourStep(id: number): TourStep | undefined {
  return TOUR_STEPS.find((s) => s.id === id);
}

/** Next step id after `id`, or null if `id` is the last step. */
export function nextStepId(id: number): number | null {
  return id >= 1 && id < TOTAL_TOUR_STEPS ? id + 1 : null;
}
